import React, {forwardRef, useImperativeHandle, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import Loading from "../../components/loading";
import {Form, Formik} from "formik";
import {attachmentCategoryRequestSchema, buildingStatTypeRequestSchema} from "../../utils/schemas";
import FormGroup from "../../components/form-group";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useGlobalNotificationMessage} from "../../context/message";
import {useBuildingStatTypeService} from "../../services/building-stat-type-service";
import {useAttachmentCategoryService} from "../../services/attachment-category-service";
import Select from "react-select";
import {useRoleService} from "../../services/role-service";
import {AttachmentCategoryTypes, BuildingOwnershipTypes} from "../../utils/constants";

const AddEditAttachmentCategoryModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState();
  const [loading, setLoading] = useState(false);
  const [attachmentCategoriesList, setAttachmentCategoriesList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [reset, setReset] = useState(0);

  let formSchema = attachmentCategoryRequestSchema;

  let attachmentCategoryService = useAttachmentCategoryService();
  let roleService = useRoleService();

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (model=null, defaultType=null) => {
    setLoading(true);

    let promises = [
      Promise.resolve('initOk'),
      roleService.getList(),
      attachmentCategoryService.getList()
    ];

    if (model && model.id){
      promises.push(getDetail(model.id));
    }

    Promise.all(promises).then(([init=null, _rolesList=[], _attachmentCategoriesList=[], detail= {}])=>{
      setAttachmentCategoriesList(_attachmentCategoriesList.filter(item=>detail?.id!==item.id));
      setRolesList(_rolesList);
      setSelectedRoles(detail.roles);
      let newDetail = Object.assign({}, {type: defaultType}, detail);
      setModel(formSchema.cast(newDetail));
      setLoading(false);
    });

    setShow(true);
  }
  const closeModal = () => {
    setShow(false);
    setReset(reset+1);
    setModel(formSchema.cast());
  }

  const getDetail = (id) => {
    return attachmentCategoryService.getDetail(id);
  }

  const selectRoles = (items=null)=>{
    setSelectedRoles([...items]);
  };

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    removeMessage();
    let reqValues = {...values};

    reqValues.sync_roles = selectedRoles?.length>0 ? selectedRoles.map((i)=>i.id) : [];

    delete reqValues.roles;
    delete reqValues.parent;

    attachmentCategoryService.addEditModel(reqValues, reqValues.id)
      .then((res) => {
        if (onSuccess && 'function' === typeof onSuccess){
          onSuccess(res);
        }
        closeModal();
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa uložiť zmeny.");
        HelperService.handleFormErrors(error, setErrors);
      })
      .finally(()=>{ setSubmitting(false);});
  };

  return (
    <>
      <Formik
        key={reset}
        enableReinitialize={true}
        initialValues={formSchema.cast(model) || formSchema.cast()}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, handleSubmit, setFieldValue, values}) => (

          <Modal size="lg" show={!!show} onHide={closeModal} centered>
            <Modal.Header closeButton>
              { model && <Modal.Title>{ !model.id ? 'Nový údaj - ' : 'Úprava údaja' }</Modal.Title>}
            </Modal.Header>
            <Modal.Body>
              <Section animation={HelperService.getRandomAnimation()}>

                {loading && <Loading className="m-2" />}

                {!loading && <Form className="row">

                  <FormGroup
                    name="title"
                    placeholder="Názov"
                    label="Názov"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <div className="form-group col-md-6">
                    <label className="form-label">Zobrazovať pre</label>
                    <Select
                      options={rolesList?.map(
                        (item)=>{
                          item.value = item.id;
                          item.label = item.name;
                          return item;
                        }
                      )}
                      placeholder={'Vyberte role'}
                      isSearchable
                      isClearable
                      closeMenuOnSelect={false}
                      isMulti
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          'borderRadius': '16px',
                        }),
                      }}
                      value={selectedRoles?.map(
                        (item)=>{
                          item.value = item.id;
                          item.label = item.name;
                          return item;
                        }
                      )}
                      onChange={selectRoles}
                    />
                  </div>

                  <FormGroup
                    name="description"
                    type="textarea"
                    placeholder="Popis"
                    label="Popis"
                    className="col-md-12"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="parent_id"
                    type="select"
                    options={attachmentCategoriesList}
                    optionLabel="title"
                    optionKey="id"
                    placeholder="Nadradená kategória"
                    label="Nadradená kategória"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="icon"
                    placeholder="icn-class"
                    label="Ikona"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="color"
                    placeholder="#fff"
                    label="Farba"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="type"
                    type="select"
                    options={Object.values(AttachmentCategoryTypes)}
                    optionLabel="title"
                    optionKey="val"
                    placeholder="skupina"
                    label="Skupina"
                    className="col-md-6"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <div className="clearfix"></div>

                </Form>}

              </Section>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
              <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>Odoslať</Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>

    </>
  );
});

export default AddEditAttachmentCategoryModal;
